import React, { createContext, useContext } from 'react';

/**
 * AppContext — single source of truth for all state shared across the editor.
 *
 * Shape (all 18 values previously prop-drilled through EditorRoute):
 *
 *   Auth / global UI
 *   ├── user               Firebase User | null
 *   ├── showToast          (message, type?, duration?) => void
 *   ├── setConfirmDialog   setState setter for the global ConfirmDialog
 *   └── onLogout           () => void
 *
 *   Profile data
 *   ├── profileData        object | null
 *   ├── setProfileData     setState setter
 *   ├── isSaving           boolean
 *   ├── fieldSaveStatus    { [fieldName]: 'saving' | 'saved' | null }
 *   └── originalUsername   string
 *
 *   Username UX
 *   ├── checkUsernameAvailability  (username) => void
 *   ├── usernameError              string | null
 *   ├── setUsernameError           setState setter
 *   ├── isCheckingUsername         boolean
 *   ├── setIsCheckingUsername      setState setter
 *   ├── usernameCheckTimeoutRef    React ref
 *   ├── saveUsername               () => void
 *   └── savingUsername             boolean
 *
 *   Save refs (mutable, not reactive)
 *   ├── lastSavedQnaRef   React ref
 *   └── lastSaveTimeRef   React ref
 *
 * Performance: the context value is memoized in App.jsx via useMemo so that
 * unrelated App state changes (e.g. toast array) don't create a new object
 * reference and re-render all consumers.
 *
 * Future: when EditorView is split into sub-components, split this into
 * focused contexts (AuthContext, ProfileContext, UsernameContext) so that
 * username-check re-renders only hit UsernameContext consumers.
 */

const AppContext = createContext(undefined);

/**
 * useAppContext — consume shared app state inside any component under AppContext.Provider.
 * Throws a descriptive error if called outside the provider (catches wiring mistakes early).
 */
export function useAppContext() {
  const ctx = useContext(AppContext);
  if (ctx === undefined) {
    throw new Error(
      'useAppContext must be used within <AppContext.Provider>. ' +
      'Wrap your component tree with the AppContextProvider exported from context/AppContext.jsx.'
    );
  }
  return ctx;
}

export default AppContext;
